// DropDownFilter.tsx
import React, { useMemo, useState } from "react";
import {
    Box,
    Checkbox,
    FormControl,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
    Tooltip,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

export interface Option {
    label: string;
    value: string;
    disabled?: boolean;
}

interface DropDownFilterProps {
    placeholder?: string;
    options: Option[];
    value: string[];
    onChange: (next: string[]) => void;
    maxMenuHeight?: number;
    showSelectAll?: boolean;
    disabled?: boolean;
}

const CustomArrow: React.FC<{ open?: boolean }> = ({ open }) => (
    <ArrowDropDownIcon
        sx={{
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
        }}
    />
);

const DropDownFilter: React.FC<DropDownFilterProps> = ({
    placeholder = "Select...",
    options,
    value,
    onChange,
    maxMenuHeight = 350,
    showSelectAll = true,
    disabled,
}) => {
    const [search, setSearch] = useState("");

    const filtered = useMemo(() => {
        if (!search.trim()) return options;
        const q = search.toLowerCase();
        return options.filter(
            (o) =>
                o.label.toLowerCase().includes(q) ||
                o.value.toLowerCase().includes(q)
        );
    }, [options, search]);

    const enabledValues = useMemo(
        () => options.filter((o) => !o.disabled).map((o) => o.value),
        [options]
    );

    const allSelected =
        enabledValues.length > 0 && enabledValues.every((v) => value.includes(v));
    const someSelected = value.length > 0 && !allSelected;

    const handleSelectAll = () => {
        if (allSelected) {
            onChange(value.filter((v) => !enabledValues.includes(v)));
        } else {
            const preservedDisabled = value.filter(
                (v) => !enabledValues.includes(v)
            );
            onChange(Array.from(new Set([...preservedDisabled, ...enabledValues])));
        }
    };

    const handleChange = (event: SelectChangeEvent<string[]>) => {
        const next = event.target.value as string[];
        onChange(next);
    };

    const renderValue = (selected: string[]) => {
        if (!selected.length) {
            return (
                <Typography
                    sx={{
                        color: "text.secondary",
                        width: "100%",
                        textAlign: "left",
                    }}
                >
                    {placeholder}
                </Typography>
            );
        }
        return options
            .filter((o) => selected.includes(o.value))
            .map((o) => o.label)
            .join(", ");
    };

    return (
        <Tooltip
            title={
                value.length
                    ? options
                        .filter((o) => value.includes(o.value))
                        .map((o) => o.label)
                        .join(", ")
                    : placeholder
            }
            placement="top"
            arrow
        >
            <FormControl fullWidth disabled={disabled}>

                <Select
                    multiple
                    displayEmpty
                    value={value}
                    onChange={handleChange}
                    input={
                        <OutlinedInput
                            sx={{
                                height: 44,
                                "& .MuiSelect-select": {
                                    display: "flex",
                                    alignItems: "center",
                                    height: "100%",
                                    paddingY: 0,
                                },
                            }}
                        />
                    }
                    renderValue={renderValue}
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: maxMenuHeight,
                                width: 320,
                                paddingTop: 0,
                                paddingBottom: 0,
                            },
                        },
                    }}
                    IconComponent={(props) => <CustomArrow open={props.open} />}
                >
                    {/* Search box */}
                    <Box sx={{ px: 2, pb: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </Box>

                    {/* Select All */}
                    {showSelectAll && options.length > 0 && (
                        <MenuItem onClick={handleSelectAll} dense>
                            <Checkbox checked={allSelected} indeterminate={someSelected} />
                            <ListItemText primary="Select all" />
                        </MenuItem>
                    )}

                    {/* Options with Tooltip */}
                    {filtered.length ? (
                        filtered.map((opt) => (
                            <MenuItem
                                key={opt.value}
                                value={opt.value}
                                disabled={opt.disabled}
                                dense
                            >
                                <Checkbox checked={value.includes(opt.value)} />
                                <Tooltip title={opt.label} placement="right" arrow>
                                    <ListItemText primary={opt.label} />
                                </Tooltip>
                            </MenuItem>
                        ))
                    ) : (
                        <MenuItem disabled dense>
                            <ListItemText primary="No results" />
                        </MenuItem>
                    )}
                </Select>
            </FormControl>
        </Tooltip>
    );
};

export default DropDownFilter;
